import { useState } from "react";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Alert from "@cloudscape-design/components/alert";
import Link from "@cloudscape-design/components/link";
import { useTranslation } from "react-i18next";

function AuthSection({ onLoginSuccess, config }) {
  const { t } = useTranslation();
  const [view, setView] = useState("login"); // 'login', 'signup', 'verify', 'newPassword'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [challengeSession, setChallengeSession] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const showError = (message) => {
    setError(message);
    setSuccess("");
    setTimeout(() => setError(""), 5000);
  };

  const showSuccess = (message) => {
    setSuccess(message);
    setError("");
    setTimeout(() => setSuccess(""), 5000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-amz-json-1.1",
          "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
        },
        body: JSON.stringify({
          AuthFlow: "USER_PASSWORD_AUTH",
          ClientId: config.userPoolClientId,
          AuthParameters: { USERNAME: email, PASSWORD: password },
        }),
      });

      const data = await response.json();

      if (response.ok && data.AuthenticationResult) {
        showSuccess(t("messages.auth.loginSuccess"));
        setTimeout(() => onLoginSuccess(data.AuthenticationResult.IdToken, email), 500);
      } else if (response.ok && data.ChallengeName === "NEW_PASSWORD_REQUIRED") {
        setChallengeSession(data.Session);
        setView("newPassword");
        setPassword("");
        showSuccess(t("auth.new-password.setSuccess"));
      } else {
        showError(t("errors.auth.loginFailed") + ": " + (data.message || data.__type || t("errors.auth.invalidInput")));
      }
    } catch (err) {
      showError(t("errors.auth.loginFailed") + ": " + t("errors.auth.networkError"));
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-amz-json-1.1",
          "X-Amz-Target": "AWSCognitoIdentityProviderService.SignUp",
        },
        body: JSON.stringify({
          ClientId: config.userPoolClientId,
          Username: email,
          Password: password,
          UserAttributes: [{ Name: "email", Value: email }],
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setPendingEmail(email);
        setView("verify");
        showSuccess(t("messages.auth.signupSuccess"));
        setEmail("");
        setPassword("");
      } else {
        showError(t("errors.auth.signupFailed") + ": " + (data.message || t("errors.auth.invalidInput")));
      }
    } catch (err) {
      showError(t("errors.auth.signupFailed") + ": " + t("errors.auth.networkError"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-amz-json-1.1",
          "X-Amz-Target": "AWSCognitoIdentityProviderService.ConfirmSignUp",
        },
        body: JSON.stringify({
          ClientId: config.userPoolClientId,
          Username: pendingEmail,
          ConfirmationCode: verificationCode,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        showSuccess(t("messages.auth.verifySuccess"));
        setVerificationCode("");
        setTimeout(() => setView("login"), 1500);
      } else {
        showError(t("errors.auth.verifyFailed") + ": " + (data.message || t("errors.auth.invalidInput")));
      }
    } catch (err) {
      showError(t("errors.auth.verifyFailed") + ": " + t("errors.auth.networkError"));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-amz-json-1.1",
          "X-Amz-Target": "AWSCognitoIdentityProviderService.ResendConfirmationCode",
        },
        body: JSON.stringify({
          ClientId: config.userPoolClientId,
          Username: pendingEmail,
        }),
      });

      if (response.ok) {
        showSuccess(t("messages.auth.codeResent"));
      } else {
        const data = await response.json();
        showError(t("errors.auth.resendFailed") + ": " + (data.message || t("errors.auth.invalidInput")));
      }
    } catch (err) {
      showError(t("errors.auth.resendFailed") + ": " + t("errors.auth.networkError"));
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-amz-json-1.1",
          "X-Amz-Target": "AWSCognitoIdentityProviderService.RespondToAuthChallenge",
        },
        body: JSON.stringify({
          ChallengeName: "NEW_PASSWORD_REQUIRED",
          ClientId: config.userPoolClientId,
          Session: challengeSession,
          ChallengeResponses: {
            USERNAME: email,
            NEW_PASSWORD: newPassword,
          },
        }),
      });

      const data = await response.json();

      if (response.ok && data.AuthenticationResult) {
        showSuccess(t("messages.auth.loginSuccess"));
        setTimeout(() => onLoginSuccess(data.AuthenticationResult.IdToken, email), 500);
      } else {
        showError(t("errors.auth.newPasswordFailed") + ": " + (data.message || data.__type || t("errors.auth.invalidInput")));
      }
    } catch (err) {
      showError(t("errors.auth.newPasswordFailed") + ": " + t("errors.auth.networkError"));
    } finally {
      setLoading(false);
    }
  };

  const handleMidwayLogin = () => {
    // Construct OAuth authorize URL
    const authorizeUrl = new URL(`https://${config.cognitoDomain}/oauth2/authorize`);
    authorizeUrl.searchParams.append("client_id", config.userPoolClientId);
    authorizeUrl.searchParams.append("response_type", "code");
    authorizeUrl.searchParams.append("scope", "openid email");
    authorizeUrl.searchParams.append("redirect_uri", `${window.location.origin}${config.callbackPath || "/callback"}`);
    authorizeUrl.searchParams.append("identity_provider", "AmazonFederate");

    // Redirect to Cognito OAuth authorize endpoint
    window.location.href = authorizeUrl.toString();
  };

  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", padding: "20px" }}>
      <div style={{ maxWidth: "420px", width: "100%" }}>
        <Container
          header={
            <Header
              variant="h1"
              description={
                view === "login"
                  ? t("auth.login.description")
                  : view === "signup"
                    ? t("auth.signup.description")
                    : view === "newPassword"
                      ? t("auth.new-password.description")
                      : `${t("auth.verify.verifyingEmail")} ${pendingEmail}`
              }
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <img src="/avatar192.png" alt="" width="64" height="64" style={{ borderRadius: "8px" }} />
                {t("venue.claude.appTitle")}
              </div>
            </Header>
          }
        >
          <SpaceBetween size="l">
            {error && <Alert type="error">{error}</Alert>}
            {success && <Alert type="success">{success}</Alert>}

            {view === "login" && (
              <SpaceBetween size="l">
                {config.midwayEnabled && (
                  <>
                    <Button variant="primary" onClick={handleMidwayLogin} fullWidth disabled={loading}>
                      {t("auth.midway.button")}
                    </Button>
                    <div style={{ textAlign: "center", margin: "10px 0", color: "#5f6b7a" }}>{t("auth.midway.or")}</div>
                  </>
                )}

                <form onSubmit={handleLogin}>
                  <SpaceBetween size="l">
                    <FormField label={t("auth.login.email")}>
                      <Input
                        value={email}
                        onChange={({ detail }) => setEmail(detail.value)}
                        type="email"
                        placeholder={t("auth.login.emailPlaceholder")}
                        disabled={loading}
                      />
                    </FormField>

                    <FormField label={t("auth.login.password")}>
                      <Input
                        value={password}
                        onChange={({ detail }) => setPassword(detail.value)}
                        type="password"
                        placeholder={t("auth.login.passwordPlaceholder")}
                        disabled={loading}
                      />
                    </FormField>

                    <Button variant="primary" loading={loading} formAction="submit" fullWidth>
                      {t("auth.login.button")}
                    </Button>

                    {!config.midwayEnabled && (
                      <div style={{ textAlign: "center" }}>
                        {t("auth.common.noAccount")}{" "}
                        <Link onFollow={() => setView("signup")} variant="primary">
                          {t("auth.signup.button")}
                        </Link>
                      </div>
                    )}
                  </SpaceBetween>
                </form>
              </SpaceBetween>
            )}

            {view === "signup" && (
              <form onSubmit={handleSignup}>
                <SpaceBetween size="l">
                  <FormField label={t("auth.login.email")}>
                    <Input
                      value={email}
                      onChange={({ detail }) => setEmail(detail.value)}
                      type="email"
                      placeholder={t("auth.login.emailPlaceholder")}
                      disabled={loading}
                    />
                  </FormField>

                  <FormField label={t("auth.login.password")}>
                    <Input
                      value={password}
                      onChange={({ detail }) => setPassword(detail.value)}
                      type="password"
                      placeholder={t("auth.login.passwordPlaceholder")}
                      disabled={loading}
                    />
                  </FormField>

                  <Button variant="primary" loading={loading} formAction="submit" fullWidth>
                    {t("auth.signup.createAccount")}
                  </Button>

                  <div style={{ textAlign: "center" }}>
                    {t("auth.common.haveAccount")}{" "}
                    <Link onFollow={() => setView("login")} variant="primary">
                      {t("auth.login.button")}
                    </Link>
                  </div>
                </SpaceBetween>
              </form>
            )}

            {view === "verify" && (
              <form onSubmit={handleVerify}>
                <SpaceBetween size="l">
                  <FormField label={t("auth.verify.code")}>
                    <Input
                      value={verificationCode}
                      onChange={({ detail }) => setVerificationCode(detail.value)}
                      type="text"
                      placeholder={t("auth.verify.codePlaceholder")}
                      disabled={loading}
                    />
                  </FormField>

                  <Button variant="primary" loading={loading} formAction="submit" fullWidth>
                    {t("auth.verify.button")}
                  </Button>

                  <div style={{ textAlign: "center" }}>
                    <Link onFollow={handleResendCode} variant="primary" disabled={loading}>
                      {t("auth.verify.resendCode")}
                    </Link>
                  </div>

                  <div style={{ textAlign: "center" }}>
                    <Link onFollow={() => setView("login")} variant="primary">
                      {t("auth.common.backToLogin")}
                    </Link>
                  </div>
                </SpaceBetween>
              </form>
            )}

            {view === "newPassword" && (
              <form onSubmit={handleNewPassword}>
                <SpaceBetween size="l">
                  <Alert type="info">{t("auth.new-password.requiresNew")}</Alert>

                  <FormField label={t("auth.new-password.label")}>
                    <Input
                      value={newPassword}
                      onChange={({ detail }) => setNewPassword(detail.value)}
                      type="password"
                      placeholder={t("auth.new-password.placeholder")}
                      disabled={loading}
                    />
                  </FormField>

                  <Button variant="primary" loading={loading} formAction="submit" fullWidth>
                    {t("auth.new-password.button")}
                  </Button>

                  <div style={{ textAlign: "center" }}>
                    <Link onFollow={() => setView("login")} variant="primary">
                      {t("auth.common.backToLogin")}
                    </Link>
                  </div>
                </SpaceBetween>
              </form>
            )}
          </SpaceBetween>
        </Container>
      </div>
    </div>
  );
}

export default AuthSection;
